/**
 * What to do with a value longer than its PDF field's character limit (/MaxLen).
 *
 * USCIS forms occasionally cap a field below the real length of its data (the
 * I-192 VOLAG Number takes 10 characters, the number has 12), so the policy is
 * set per field mapping in Settings instead of being hard-coded per form.
 */
export const OverflowPolicy = {
    // The box shows what fits within the limit; the full value goes to the addendum
    ADDENDUM: "addendum",
    // The box shows what fits within the limit and the rest is dropped
    TRUNCATE: "truncate",
    // The limit is removed when the PDF is generated; the text shrinks to fit the box
    IGNORE_MAX_LENGTH: "ignoreMaxLength",
};

/** A mapping without a policy (null) behaves like this one. */
export const DEFAULT_OVERFLOW_POLICY = OverflowPolicy.ADDENDUM;

export const OVERFLOW_POLICY_OPTIONS = [
    {
        value: OverflowPolicy.ADDENDUM,
        label: "Continue in the addendum",
        description: "The box shows the characters that fit within the limit and the full value is added to the addendum.",
    },
    {
        value: OverflowPolicy.TRUNCATE,
        label: "Cut at the limit",
        description: "The box shows the characters that fit within the limit and the rest is dropped.",
    },
    {
        value: OverflowPolicy.IGNORE_MAX_LENGTH,
        label: "Ignore the limit",
        description:
            "Use it when the form's limit is wrong. The full value is printed in the box, with smaller text if needed. A field split into boxes is printed as plain text.",
    },
];

export const getOverflowPolicy = (policy) =>
    Object.values(OverflowPolicy).includes(policy) ? policy : DEFAULT_OVERFLOW_POLICY;

/**
 * Removes a field's /MaxLen before it is filled, for the "ignoreMaxLength" policy.
 * A combed field divides its width by /MaxLen and cannot exist without one, so it
 * becomes a plain text field first.
 */
export const removeMaxLength = (field) => {
    if (field.getMaxLength() === undefined) return;
    if (field.isCombed()) field.disableCombing();
    field.setMaxLength(undefined);
};

/** `text` cut at `maxLength`, when the field has one: pdf-lib's setText throws past it. */
export const withinMaxLength = (text, maxLength) => (maxLength > 0 ? text.slice(0, maxLength) : text);

/** { [pdfField]: policy } for the mappings that set one. */
export const buildOverflowPolicies = (formFieldMappings) =>
    (formFieldMappings || []).reduce((acc, mapping) => {
        if (mapping.overflowPolicy) acc[mapping.pdfField] = mapping.overflowPolicy;
        return acc;
    }, {});

/**
 * Warning shown on a field whose value exceeds its limit, or null when it does not.
 *
 * A field that ignores its limit only warns when it is combed: its boxes cannot
 * show the extra characters while editing, although the PDF prints them all.
 */
export const describeOverLimit = ({ policy, maxLength, length, isCombed = false }) => {
    if (!(maxLength > 0) || length <= maxLength) return null;

    const summary = isCombed
        ? `This field has ${maxLength} boxes and the value has ${length} characters.`
        : `This field accepts ${maxLength} characters and the value has ${length}.`;

    switch (getOverflowPolicy(policy)) {
        case OverflowPolicy.TRUNCATE:
            return `${summary} Only the first ${maxLength} will be printed.`;
        case OverflowPolicy.IGNORE_MAX_LENGTH:
            return isCombed ? `${summary} The full value is printed as plain text across the field.` : null;
        default:
            return `${summary} The PDF shows the first ${maxLength} in the box and the full value in the addendum.`;
    }
};
